/**
 * Part-time overtime trigger — one algebra per award.
 *
 * Do not share "hours beyond rostered" or "hours beyond 10" across awards.
 * SCHADS pays agreed extra at ordinary until 10h/day or 38/76.
 * Aged Care treats extra beyond that day's roster as OT unless cl 10.3(c)
 * written variation. Nurses OT only beyond full-time daily hours (default 8).
 *
 * Artifact:
 * docs/operations/clause-verifications/2026-08-15-aged-care-legislation-award-algebra-fixtures.md
 */

import {
  allocateOrdinaryHoursUnderCapCascade,
  allocateOrdinaryHoursUnderCapCascadeLegacyMidnightDoubleCount,
  type ResolvedOrdinaryHoursCapWindow,
} from './ordinary-hours-cap-windows';

export const AWARD_PART_TIME_OVERTIME_VERSION = '1.1.0';

export type PartTimeOvertimeSplit = {
  ordinaryHours: number;
  overtimeHours: number;
  clauseRef: string;
};

function round4(n: number): number {
  return Math.round(n * 10_000) / 10_000;
}

function split(worked: number, overtime: number): { ordinaryHours: number; overtimeHours: number } {
  const safeWorked = Number.isFinite(worked) && worked > 0 ? worked : 0;
  const safeOt = Math.min(safeWorked, Math.max(0, overtime));
  return { ordinaryHours: round4(safeWorked - safeOt), overtimeHours: round4(safeOt) };
}

/**
 * SCHADS — agreed extra hours at ordinary. OT only beyond 10h that day
 * or 38h that week (76/fortnight is the caller's weekly-equivalent).
 */
export function schadsPartTimeOvertime(input: {
  hoursWorked: number;
  ordinaryHoursAlreadyThisWeek?: number;
  dailyOrdinaryCap?: number;
  weeklyOrdinaryCap?: number;
}): PartTimeOvertimeSplit {
  const dailyCap = input.dailyOrdinaryCap ?? 10;
  const weeklyCap = input.weeklyOrdinaryCap ?? 38;
  const prior = Math.max(0, input.ordinaryHoursAlreadyThisWeek ?? 0);
  const dailyOt = Math.max(0, input.hoursWorked - dailyCap);
  const weeklyOt = Math.max(0, prior + input.hoursWorked - weeklyCap);
  const overtimeHours = Math.max(dailyOt, weeklyOt);
  return { ...split(input.hoursWorked, overtimeHours), clauseRef: 'MA000100 cl 28.1(b)' };
}

/**
 * Aged Care cl 25.1(b)(iii) — time beyond that day's rostered hours is OT
 * unless the parties varied the roster in writing under cl 10.3(c).
 * cl 25.1(b)(i)/(ii) still apply (38/76 and 10h/day) even with a variation.
 */
export function agedCarePartTimeOvertime(input: {
  hoursWorked: number;
  rosteredHoursThatDay: number;
  writtenVariationAgreed?: boolean;
  ordinaryHoursAlreadyThisWeek?: number;
}): PartTimeOvertimeSplit {
  const dailyCap = 10;
  const weeklyCap = 38;
  const prior = Math.max(0, input.ordinaryHoursAlreadyThisWeek ?? 0);
  const beyondRoster = input.writtenVariationAgreed
    ? 0
    : Math.max(0, input.hoursWorked - Math.max(0, input.rosteredHoursThatDay));
  const dailyOt = Math.max(0, input.hoursWorked - dailyCap);
  const weeklyOt = Math.max(0, prior + input.hoursWorked - weeklyCap);
  const overtimeHours = Math.max(beyondRoster, dailyOt, weeklyOt);
  return { ...split(input.hoursWorked, overtimeHours), clauseRef: 'MA000018 cl 25.1(b)' };
}

/**
 * Nurses cl 19.1 — part-time overtime only beyond full-time daily hours
 * (engine default 8h/shift). Extra hours inside that ceiling stay ordinary.
 */
export function nursesPartTimeOvertime(input: {
  hoursWorked: number;
  fullTimeDailyHours?: number;
  ordinaryHoursAlreadyThisWeek?: number;
}): PartTimeOvertimeSplit {
  const dailyCap = input.fullTimeDailyHours ?? 8;
  const weeklyCap = 38;
  const prior = Math.max(0, input.ordinaryHoursAlreadyThisWeek ?? 0);
  const dailyOt = Math.max(0, input.hoursWorked - dailyCap);
  const weeklyOt = Math.max(0, prior + input.hoursWorked - weeklyCap);
  const overtimeHours = Math.max(dailyOt, weeklyOt);
  return { ...split(input.hoursWorked, overtimeHours), clauseRef: 'MA000034 cl 19.1' };
}

/**
 * Medea General Staff EA cl 26(f) — ordinary hours caps (8/38/76/152) with a
 * single cascade across the shift. Fortnight headroom is consumed once even
 * when the shift crosses midnight (8 Sep M6).
 */
export function medeaFortnightCapOvertimeSplit(input: {
  hoursWorked: number;
  capWindows: readonly ResolvedOrdinaryHoursCapWindow[];
  dailyOrdinaryCap?: number;
  midnightSegmentHours?: readonly [number, number];
  useLegacyMidnightDoubleCount?: boolean;
}): PartTimeOvertimeSplit {
  const cascade = input.useLegacyMidnightDoubleCount
    ? allocateOrdinaryHoursUnderCapCascadeLegacyMidnightDoubleCount({
        segmentHours: input.midnightSegmentHours ?? [input.hoursWorked, 0],
        capWindows: input.capWindows,
        dailyOrdinaryCap: input.dailyOrdinaryCap,
      })
    : allocateOrdinaryHoursUnderCapCascade({
        shiftHours: input.hoursWorked,
        capWindows: input.capWindows,
        dailyOrdinaryCap: input.dailyOrdinaryCap,
      });

  return {
    ordinaryHours: cascade.ordinaryHours,
    overtimeHours: cascade.overtimeHours,
    clauseRef: 'Medea General Staff EA cl 26(f)',
  };
}
